import { useEffect, useState } from "react";
import api, { fmtBRL, fmtInt, fmtNum } from "../lib/api";
import PageHeader from "../components/PageHeader";
import { toast } from "sonner";
import { RefreshCw } from "lucide-react";

export default function Purchases() {
  const [planning, setPlanning] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);

  const load = () => {
    setLoading(true);
    Promise.all([
      api.get("/purchase-planning").then(r => r.data),
      api.get("/purchases").then(r => r.data),
    ]).then(([p, i]) => {
      setPlanning(p);
      setInvoices(i);
    }).finally(() => setLoading(false));
  };
  useEffect(load, []);

  const sync = async () => {
    setSyncing(true);
    try {
      const r = await api.post("/sync/omie/purchases", {});
      toast.success(`${r.data.count} NFs sincronizadas do Omie (${r.data.start_date} → ${r.data.end_date})`);
      load();
    } catch (e) {
      toast.error("Erro ao sincronizar do Omie");
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div data-testid="purchases-page">
      <PageHeader
        eyebrow="Compras"
        title="ENTRADAS E PLANEJAMENTO"
        accent="o que está entrando."
        description="Planejamento semanal de compras e notas fiscais de entrada do Omie."
        actions={
          <button onClick={sync} className="ifl-btn" disabled={syncing} data-testid="sync-purchases-btn">
            <RefreshCw size={14} strokeWidth={2} className={syncing ? "ifl-pulse" : ""} />
            {syncing ? "Sincronizando…" : "Sincronizar NFs (Omie)"}
          </button>
        }
        testId="purchases-header"
      />

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="ifl-card overflow-hidden">
          <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--ifl-border)" }}>
            <div>
              <div className="label-caps">Planejamento semanal</div>
              <div className="font-italic-serif text-xl mt-1">o que precisa entrar.</div>
            </div>
            <span className="font-display text-xs uppercase tracking-widest" style={{ color: "var(--ifl-muted)" }}>
              {fmtInt(planning.length)} itens
            </span>
          </div>
          <div className="overflow-x-auto" style={{ maxHeight: 600 }}>
            <table className="ifl-table">
              <thead>
                <tr>
                  <th>Cat.</th>
                  <th>Item</th>
                  <th>Fornecedor</th>
                  <th className="text-right">Qtde</th>
                  <th>Un.</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={5} className="text-center py-12" style={{ color: "var(--ifl-muted)" }}>Carregando…</td></tr>
                ) : planning.slice(0, 300).map((p, idx) => (
                  <tr key={idx}>
                    <td className="font-display text-xs uppercase tracking-widest" style={{ color: "var(--ifl-muted)" }}>{p.category}</td>
                    <td>{p.item}</td>
                    <td className="text-xs">{p.supplier || "—"}</td>
                    <td className="text-right">{fmtNum(p.quantity, 0)}</td>
                    <td className="font-display text-xs uppercase" style={{ color: "var(--ifl-muted)" }}>{p.unit}</td>
                  </tr>
                ))}
                {!loading && planning.length === 0 && (
                  <tr><td colSpan={5} className="text-center py-12" style={{ color: "var(--ifl-muted)" }}>Sem planejamento importado.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        <div className="ifl-card overflow-hidden">
          <div className="px-6 py-4 border-b flex items-center justify-between" style={{ borderColor: "var(--ifl-border)" }}>
            <div>
              <div className="label-caps">Notas fiscais (Omie)</div>
              <div className="font-italic-serif text-xl mt-1">o que entrou.</div>
            </div>
            <span className="font-display text-xs uppercase tracking-widest" style={{ color: "var(--ifl-muted)" }}>
              {fmtInt(invoices.length)} NFs
            </span>
          </div>
          <div className="overflow-x-auto" style={{ maxHeight: 600 }}>
            <table className="ifl-table">
              <thead>
                <tr>
                  <th>NF</th>
                  <th>Fornecedor</th>
                  <th>Data</th>
                  <th className="text-right">Valor</th>
                </tr>
              </thead>
              <tbody>
                {invoices.slice(0, 200).map((p) => (
                  <tr key={p.id}>
                    <td className="font-display text-xs">{p.nf_number}/{p.series}</td>
                    <td>{p.supplier}</td>
                    <td className="font-display text-xs">{p.date}</td>
                    <td className="text-right font-display">{fmtBRL(p.total_value)}</td>
                  </tr>
                ))}
                {!loading && invoices.length === 0 && (
                  <tr><td colSpan={4} className="text-center py-12" style={{ color: "var(--ifl-muted)" }}>
                    Sem NFs importadas. Clique em <strong>Sincronizar NFs (Omie)</strong>.
                  </td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
